import React, { useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import { Box, IconButton, Chip, Typography, Paper, useTheme } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';

export interface ChatFile {
  id: string;
  file: File;
  preview?: string; // For images
  type: 'image' | 'document' | 'other';
}

interface FileUploadProps {
  files: ChatFile[];
  onFilesChange: (files: ChatFile[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  acceptedTypes?: string[];
  onDragStateChange?: (isDragging: boolean) => void; // Notify parent of drag state
}

export interface FileUploadRef {
  processFiles: (fileList: FileList) => Promise<void>;
}

const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(({
  files,
  onFilesChange,
  maxFiles = 5,
  maxSizeMB = 20,
  acceptedTypes = ['image/*', 'application/pdf', '.doc', '.docx', '.txt', '.csv', '.xlsx'],
  onDragStateChange,
}, ref) => {
  const theme = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Notify parent when drag state changes
  React.useEffect(() => {
    onDragStateChange?.(dragActive);
  }, [dragActive, onDragStateChange]);

  const getFileType = (file: File): 'image' | 'document' | 'other' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.includes('pdf') || 
        file.type.includes('document') || 
        file.type.includes('text') ||
        file.type.includes('spreadsheet') ||
        file.name.match(/\.(doc|docx|txt|csv|xlsx)$/i)) {
      return 'document';
    }
    return 'other';
  };

  const getFileIcon = (fileType: 'image' | 'document' | 'other', mimeType: string) => {
    if (fileType === 'image') return <ImageIcon />;
    if (mimeType.includes('pdf')) return <PictureAsPdfIcon />;
    if (fileType === 'document') return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
  };

  const processFiles = useCallback(
    async (fileList: FileList) => {
      const newFiles: ChatFile[] = [];
      
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        
        // Check file size
        if (file.size > maxSizeMB * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is ${maxSizeMB}MB`);
          continue;
        }

        // Check max files
        if (files.length + newFiles.length >= maxFiles) {
          alert(`Maximum ${maxFiles} files allowed`);
          break;
        }

        const fileType = getFileType(file);
        const chatFile: ChatFile = {
          id: `${Date.now()}-${i}`,
          file,
          type: fileType,
        };

        // Create preview for images
        if (fileType === 'image') {
          chatFile.preview = URL.createObjectURL(file);
        }

        newFiles.push(chatFile);
      }

      onFilesChange([...files, ...newFiles]);
    },
    [files, onFilesChange, maxFiles, maxSizeMB]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      const fileToRemove = files.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      onFilesChange(files.filter(f => f.id !== fileId));
    },
    [files, onFilesChange]
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Expose processFiles method to parent via ref
  useImperativeHandle(ref, () => ({
    processFiles,
  }));

  return (
    <>
      {/* File Input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Attach Button */}
      <IconButton
        onClick={handleButtonClick}
        disabled={files.length >= maxFiles}
        sx={{
          color: theme.palette.primary.main,
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
        title="Attach files"
      >
        <AttachFileIcon />
      </IconButton>

      {/* File Previews */}
      {files.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1,
            mb: 1,
          }}
        >
          {files.map((chatFile) => (
            <Paper
              key={chatFile.id}
              elevation={2}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                pr: 0.5,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                maxWidth: chatFile.type === 'image' ? '200px' : '300px',
              }}
            >
              {/* Preview or Icon */}
              {chatFile.preview ? (
                <Box
                  component="img"
                  src={chatFile.preview}
                  alt={chatFile.file.name}
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    color: theme.palette.primary.main,
                  }}
                >
                  {getFileIcon(chatFile.type, chatFile.file.type)}
                </Box>
              )}

              {/* File Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {chatFile.file.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {formatFileSize(chatFile.file.size)}
                </Typography>
              </Box>

              {/* Remove Button */}
              <IconButton
                size="small"
                onClick={() => handleRemoveFile(chatFile.id)}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    bgcolor: 'rgba(255, 0, 0, 0.1)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}

      {/* Drop Zone (only show when dragging) */}
      {dragActive && (
        <Paper
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `3px dashed ${theme.palette.primary.main}`,
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <AttachFileIcon sx={{ fontSize: '4rem', mb: 2 }} />
            <Typography variant="h5">Drop files here</Typography>
            <Typography variant="body2" color="text.secondary">
              Maximum {maxFiles} files, up to {maxSizeMB}MB each
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  );
});

// Add display name for debugging
FileUpload.displayName = 'FileUpload';

// Export a wrapper component that adds drag-drop zone to any children
export const DragDropZone: React.FC<{
  children: React.ReactNode;
  onFilesDropped: (files: FileList) => void;
  isDragging: boolean;
  onDragStateChange: (isDragging: boolean) => void;
}> = ({ children, onFilesDropped, isDragging, onDragStateChange }) => {
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      onDragStateChange(true);
    } else if (e.type === 'dragleave') {
      // Only set to false if we're leaving the container completely
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
        onDragStateChange(false);
      }
    }
  }, [onDragStateChange]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDragStateChange(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        onFilesDropped(e.dataTransfer.files);
      }
    },
    [onFilesDropped, onDragStateChange]
  );

  return (
    <Box
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{ width: '100%' }}
    >
      {children}
    </Box>
  );
};

export default FileUpload;

